import React, { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { StatusPill } from "@/components/PortalShell";
import { ChevronLeft, ChevronRight, Plus, X, Trash2, ClipboardCheck, CheckCircle2 } from "lucide-react";

const TYPES = ["Inspection", "Pool service", "Cleaning", "Garden", "Repair", "Owner arrival", "Meeting", "Delivery", "Meter reading", "Other"];
const TYPE_COLOR = {
  Inspection: "#3a5170", "Pool service": "#2f7d8a", Cleaning: "#6b4e8a", Garden: "#5B6440",
  Repair: "#8a5a1f", "Owner arrival": "#a23b5e", Meeting: "#4a4a4a", Delivery: "#726A5C",
  "Meter reading": "#7a6a3a", Other: "#555", Contractor: "#b06a1f", "Storm callout": "#3a5a99",
};
const startOfWeek = (d) => d.subtract((d.day() + 6) % 7, "day").startOf("day");

export default function StaffCalendar({ properties, onStartInspection }) {
  const [view, setView] = useState("week");
  const [cursor, setCursor] = useState(dayjs());
  const [appts, setAppts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [gcal, setGcal] = useState({ connected: false, configured: false });

  useEffect(() => {
    api.get("/calendar/status").then(({ data }) => setGcal(data)).catch(() => {});
    api.get("/contractors").then(({ data }) => setContractors(data)).catch(() => {});
    const p = new URLSearchParams(window.location.search).get("gcal");
    if (p === "connected") { toast.success("Google Calendar connected"); window.history.replaceState({}, "", window.location.pathname); }
    else if (p === "error") { toast.error("Google Calendar connection failed"); window.history.replaceState({}, "", window.location.pathname); }
  }, []);
  const connectGoogle = async () => { try { const { data } = await api.get("/calendar/auth-url"); window.location.href = data.url; } catch (e) { toast.error(apiErr(e)); } };
  const disconnectGoogle = async () => { try { await api.post("/calendar/disconnect"); toast.success("Google Calendar disconnected"); setGcal({ ...gcal, connected: false }); } catch (e) { toast.error(apiErr(e)); } };

  const range = useCallback(() => {
    if (view === "day") return [cursor.startOf("day"), cursor.endOf("day")];
    if (view === "month") { const s = startOfWeek(cursor.startOf("month")); return [s, s.add(42, "day")]; }
    const s = startOfWeek(cursor); return [s, s.add(7, "day")];
  }, [view, cursor]);

  const load = useCallback(async () => {
    const [s, e] = range();
    try {
      const [a, st, jb] = await Promise.all([
        api.get(`/appointments?start=${s.toISOString()}&end=${e.toISOString()}`),
        staff.length ? Promise.resolve({ data: staff }) : api.get("/staff-members"),
        api.get(`/jobs`),
      ]);
      const TIME_MAP = { Morning: "09:00", Afternoon: "14:00", Evening: "18:00" };
      const jobItems = (jb.data || []).filter((j) => j.scheduled_date).map((j) => {
        const start = dayjs(`${j.scheduled_date}T${TIME_MAP[j.scheduled_time] || "09:00"}`);
        return { appointment_id: `job_${j.job_id}`, _isJob: true, job: j, type: "Contractor",
          title: j.title || "Contractor visit", start: start.toISOString(), end: start.add(1, "hour").toISOString(),
          staff_name: [j.contractor_name, j.contractor_trade].filter(Boolean).join(" · "),
          status: j.status, property_id: j.property_id, cost: j.cost };
      }).filter((j) => { const d = dayjs(j.start); return !d.isBefore(s) && d.isBefore(e); });
      setAppts([...a.data, ...jobItems]); if (!staff.length) setStaff(st.data);
    } catch (err) { toast.error(apiErr(err)); }
  }, [range]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const move = (dir) => setCursor((c) => c.add(dir * (view === "month" ? 1 : view === "day" ? 1 : 7), view === "month" ? "month" : "day"));
  const apptsOn = (day) => appts.filter((a) => dayjs(a.start).isSame(day, "day")).sort((x, y) => x.start.localeCompare(y.start));

  const save = async (payload, force = false) => {
    try {
      if (payload.appointment_id) await api.patch(`/appointments/${payload.appointment_id}?force=${force}`, payload);
      else await api.post(`/appointments?force=${force}`, payload);
      toast.success("Saved to calendar"); setForm(null); setConflicts(null); load();
    } catch (err) {
      if (err?.response?.status === 409) { setConflicts({ payload, list: err.response.data.detail.conflicts }); }
      else toast.error(apiErr(err));
    }
  };
  const removeAppt = async (id) => { try { await api.delete(`/appointments/${id}`); toast.success("Removed"); setDetail(null); load(); } catch (e) { toast.error(apiErr(e)); } };
  const markStatus = async (a, status) => { try { await api.patch(`/appointments/${a.appointment_id}`, { status }); toast.success(status === "completed" ? "Marked complete" : "Updated"); setDetail(null); load(); } catch (e) { toast.error(apiErr(e)); } };
  const startInspection = async (a) => { await api.patch(`/appointments/${a.appointment_id}`, { status: "completed" }).catch(() => {}); setDetail(null); onStartInspection(a.property_id); };

  const title = view === "month" ? cursor.format("MMMM YYYY") : view === "day" ? cursor.format("dddd DD/MM/YY")
    : `${startOfWeek(cursor).format("DD/MM")} - ${startOfWeek(cursor).add(6, "day").format("DD/MM/YY")}`;

  return (
    <div data-testid="staff-calendar">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button className="cal-nav" onClick={() => move(-1)} data-testid="cal-prev"><ChevronLeft size={18} /></button>
          <button className="btn-outline" style={{ padding: "6px 14px" }} onClick={() => setCursor(dayjs())} data-testid="cal-today">Today</button>
          <button className="cal-nav" onClick={() => move(1)} data-testid="cal-next"><ChevronRight size={18} /></button>
          <h2 style={{ fontSize: 24, margin: "0 0 0 8px" }} data-testid="cal-title">{title}</h2>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ display: "flex", border: "1px solid var(--line)", borderRadius: 999, overflow: "hidden" }}>
            {["week", "month", "day"].map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`} style={{ cursor: "pointer", padding: "7px 16px", fontSize: 12, textTransform: "capitalize", border: "none", background: view === v ? "var(--ink)" : "transparent", color: view === v ? "var(--paper)" : "var(--ink)" }}>{v}</button>
            ))}
          </div>
          {gcal.configured && (gcal.connected
            ? <button className="btn-outline" style={{ padding: "8px 14px", display: "inline-flex", alignItems: "center", gap: 6 }} onClick={disconnectGoogle} data-testid="gcal-disconnect" title={gcal.email || ""}><span style={{ color: "#2f7d3a", fontSize: 16 }}>●</span> Google connected</button>
            : <button className="btn-outline" style={{ padding: "8px 14px" }} onClick={connectGoogle} data-testid="gcal-connect">Connect Google Calendar</button>)}
          <button className="btn-ink" style={{ padding: "8px 16px", display: "inline-flex", alignItems: "center", gap: 6 }} onClick={() => setForm({ type: "Inspection", staff_id: staff[0]?.user_id || "", property_id: "", date: cursor.format("YYYY-MM-DD"), startTime: "09:00", endTime: "10:00", title: "", notes: "", contractor_id: "" })} data-testid="new-appointment-btn"><Plus size={16} /> New appointment</button>
        </div>
      </div>

      {view === "week" && <WeekView cursor={cursor} apptsOn={apptsOn} onOpen={setDetail} />}
      {view === "month" && <MonthView cursor={cursor} apptsOn={apptsOn} onOpen={setDetail} onDay={(d) => { setCursor(d); setView("day"); }} />}
      {view === "day" && <DayView cursor={cursor} apptsOn={apptsOn} onOpen={setDetail} />}

      {form && <ApptForm form={form} setForm={setForm} properties={properties} staff={staff} contractors={contractors} onSave={save} />}
      {detail && <ApptDetail a={detail} onClose={() => setDetail(null)} onDelete={removeAppt} onStatus={markStatus} onEdit={(a) => { setDetail(null); setForm(toForm(a)); }} onStartInspection={startInspection} propName={(properties.find((p) => p.property_id === detail.property_id) || {}).name} />}
      {conflicts && <ConflictModal data={conflicts} onCancel={() => setConflicts(null)} onForce={() => save(conflicts.payload, true)} />}
    </div>
  );
}

const toForm = (a) => ({ appointment_id: a.appointment_id, type: a.type, staff_id: a.staff_id, property_id: a.property_id || "", date: dayjs(a.start).format("YYYY-MM-DD"), startTime: dayjs(a.start).format("HH:mm"), endTime: dayjs(a.end).format("HH:mm"), title: a.title, notes: a.notes || "", contractor_id: a.contractor_id || "" });

const Chip = ({ a, onOpen }) => {
  const done = a.status === "completed";
  return (
  <button onClick={() => onOpen(a)} data-testid="appt-chip" style={{ textAlign: "left", cursor: "pointer", border: "none", borderLeft: `3px solid ${TYPE_COLOR[a.type] || "#555"}`, background: done ? "#eef1ea" : "var(--soft)", padding: "5px 8px", borderRadius: 3, width: "100%" }}>
    <div style={{ fontSize: 11, color: "var(--muted)", display: "flex", justifyContent: "space-between" }}><span>{dayjs(a.start).format("HH:mm")}</span>{done && <span style={{ color: "#5B6440", fontWeight: 600 }}>✓ Done</span>}</div>
    <div style={{ fontSize: 12.5, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: done ? "line-through" : "none", opacity: done ? 0.65 : 1 }}>{a.title}</div>
    <div style={{ fontSize: 10.5, color: "var(--muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.staff_name}</div>
  </button>
  );
};

function WeekView({ cursor, apptsOn, onOpen }) {
  const s = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => s.add(i, "day"));
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 8, overflowX: "auto" }}>
      {days.map((d) => {
        const today = d.isSame(dayjs(), "day");
        return (
          <div key={d.toISOString()} style={{ minWidth: 120, border: "1px solid var(--line)", borderRadius: 4, background: "var(--paper)" }}>
            <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--line)", background: today ? "var(--ink)" : "transparent", color: today ? "var(--paper)" : "var(--ink)", borderRadius: "4px 4px 0 0" }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".08em", opacity: 0.7 }}>{d.format("ddd")}</div>
              <div style={{ fontSize: 18 }}>{d.format("D")}</div>
            </div>
            <div style={{ padding: 6, display: "grid", gap: 5, minHeight: 90 }}>
              {apptsOn(d).map((a) => <Chip key={a.appointment_id} a={a} onOpen={onOpen} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayView({ cursor, apptsOn, onOpen }) {
  const items = apptsOn(cursor);
  return (
    <div style={{ display: "grid", gap: 8, maxWidth: 640 }}>
      {items.length === 0 && <p style={{ color: "var(--muted)" }}>Nothing scheduled for this day.</p>}
      {items.map((a) => (
        <button key={a.appointment_id} onClick={() => onOpen(a)} data-testid="appt-chip" style={{ textAlign: "left", cursor: "pointer", display: "grid", gridTemplateColumns: "80px 1fr auto", gap: 12, alignItems: "center", border: "1px solid var(--line)", borderLeft: `4px solid ${TYPE_COLOR[a.type] || "#555"}`, background: "var(--paper)", padding: "12px 14px", borderRadius: 4 }}>
          <div style={{ fontSize: 13, color: "var(--muted)" }}>{dayjs(a.start).format("HH:mm")}-{dayjs(a.end).format("HH:mm")}</div>
          <div><div style={{ fontWeight: 500 }}>{a.title}</div><div style={{ fontSize: 12, color: "var(--muted)" }}>{a.type} · {a.staff_name}</div></div>
          <StatusPill status={a.status} />
        </button>
      ))}
    </div>
  );
}

function MonthView({ cursor, apptsOn, onOpen, onDay }) {
  const s = startOfWeek(cursor.startOf("month"));
  const days = Array.from({ length: 42 }, (_, i) => s.add(i, "day"));
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 6, marginBottom: 6 }}>
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="lbl-m" style={{ textAlign: "center" }}>{d}</div>)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 6 }}>
        {days.map((d) => {
          const list = apptsOn(d); const other = d.month() !== cursor.month(); const today = d.isSame(dayjs(), "day");
          return (
            <div key={d.toISOString()} onClick={() => onDay(d)} style={{ cursor: "pointer", minHeight: 92, border: "1px solid var(--line)", borderRadius: 4, padding: 6, background: other ? "var(--soft)" : "var(--paper)", opacity: other ? 0.55 : 1 }}>
              <div style={{ fontSize: 12, fontWeight: today ? 700 : 400, color: today ? "var(--honey)" : "var(--ink)", marginBottom: 4 }}>{d.format("D")}</div>
              <div style={{ display: "grid", gap: 3 }}>
                {list.slice(0, 3).map((a) => (
                  <div key={a.appointment_id} onClick={(e) => { e.stopPropagation(); onOpen(a); }} data-testid="appt-chip" style={{ fontSize: 10.5, padding: "2px 5px", borderRadius: 2, borderLeft: `3px solid ${TYPE_COLOR[a.type] || "#555"}`, background: "var(--soft)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{dayjs(a.start).format("HH:mm")} {a.title}</div>
                ))}
                {list.length > 3 && <div style={{ fontSize: 10, color: "var(--muted)" }}>+{list.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const Modal = ({ children, onClose, testid }) => (
  <div onClick={onClose} data-testid={testid} style={{ position: "fixed", inset: 0, zIndex: 150, background: "rgba(20,17,14,.5)", display: "grid", placeItems: "center", padding: 16 }}>
    <div onClick={(e) => e.stopPropagation()} className="card-soft" style={{ padding: 24, width: "100%", maxWidth: 460, maxHeight: "90vh", overflowY: "auto" }}>{children}</div>
  </div>
);

function ApptForm({ form, setForm, properties, staff, contractors = [], onSave }) {
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = () => {
    if (!form.staff_id) return toast.error("Assign a staff member");
    const start = dayjs(`${form.date}T${form.startTime}`); const end = dayjs(`${form.date}T${form.endTime}`);
    if (!end.isAfter(start)) return toast.error("End time must be after start");
    const pname = (properties.find((p) => p.property_id === form.property_id) || {}).name;
    const title = form.title || `${form.type}${pname ? ", " + pname : ""}`;
    onSave({ appointment_id: form.appointment_id, property_id: form.property_id || null, title, type: form.type, staff_id: form.staff_id, contractor_id: form.contractor_id || null, start: start.toISOString(), end: end.toISOString(), notes: form.notes });
  };
  return (
    <Modal onClose={() => setForm(null)} testid="appt-form">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div className="lbl">{form.appointment_id ? "Edit appointment" : "New appointment"}</div>
        <button onClick={() => setForm(null)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} /></button>
      </div>
      <div style={{ display: "grid", gap: 12 }}>
        <select className="inp" value={form.type} onChange={upd("type")} data-testid="appt-type">{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
        <select className="inp" value={form.property_id} onChange={upd("property_id")} data-testid="appt-property"><option value="">General / no property</option>{properties.map((p) => <option key={p.property_id} value={p.property_id}>{p.ref ? p.ref + " · " : ""}{p.name}</option>)}</select>
        <select className="inp" value={form.staff_id} onChange={upd("staff_id")} data-testid="appt-staff"><option value="">Assign to…</option>{staff.map((s) => <option key={s.user_id} value={s.user_id}>{s.name}</option>)}</select>
        <select className="inp" value={form.contractor_id || ""} onChange={upd("contractor_id")} data-testid="appt-contractor"><option value="">No contractor</option>{contractors.map((c) => <option key={c.contractor_id} value={c.contractor_id}>{c.name}{c.trade ? ` · ${c.trade}` : ""}{c.email ? ` · ${c.email}` : ""}</option>)}</select>
        {form.contractor_id && <div style={{ fontSize: 11, color: "var(--muted)", marginTop: -4 }}>We'll email this contractor the appointment details automatically, and add them to the Google Calendar invite.</div>}
        <input className="inp" type="date" value={form.date} onChange={upd("date")} data-testid="appt-date" />
        <div style={{ display: "flex", gap: 10 }}>
          <input className="inp" type="time" value={form.startTime} onChange={upd("startTime")} data-testid="appt-start" />
          <input className="inp" type="time" value={form.endTime} onChange={upd("endTime")} data-testid="appt-end" />
        </div>
        <input className="inp" placeholder="Title (optional, auto-filled)" value={form.title} onChange={upd("title")} data-testid="appt-title" />
        <textarea className="inp" rows={2} placeholder="Notes (optional)" value={form.notes} onChange={upd("notes")} />
        <button className="btn-ink" onClick={submit} style={{ justifySelf: "start" }} data-testid="appt-save">{form.appointment_id ? "Save changes" : "Schedule"}</button>
      </div>
    </Modal>
  );
}

function ApptDetail({ a, onClose, onDelete, onStatus, onEdit, onStartInspection, propName }) {
  if (a._isJob) {
    const j = a.job || {};
    return (
      <Modal onClose={onClose} testid="appt-detail">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 12 }}>
          <div>
            <span className="lbl-m" style={{ color: TYPE_COLOR.Contractor }}>Contractor visit</span>
            <h3 style={{ fontSize: 22, margin: "4px 0" }}>{a.title}</h3>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} /></button>
        </div>
        <div style={{ display: "grid", gap: 6, fontSize: 14, marginBottom: 16 }}>
          <div>{dayjs(a.start).format("ddd DD/MM/YY")}{j.scheduled_time ? ` · ${j.scheduled_time}` : ""}</div>
          <div style={{ color: "var(--muted)" }}>{a.staff_name}{j.property_name ? ` · ${j.property_name}` : ""}</div>
          {j.description && <div style={{ color: "var(--muted)" }}>{j.description}</div>}
          {a.cost != null && <div>Cost: €{Number(a.cost).toFixed(2)}</div>}
          <StatusPill status={a.status} />
        </div>
        <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 0 }}>Manage this job in the Contractors tab.</p>
      </Modal>
    );
  }
  return (
    <Modal onClose={onClose} testid="appt-detail">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 12 }}>
        <div>
          <span className="lbl-m" style={{ color: TYPE_COLOR[a.type] || "#555" }}>{a.type}</span>
          <h3 style={{ fontSize: 22, margin: "4px 0" }}>{a.title}</h3>
        </div>
        <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} /></button>
      </div>
      <div style={{ display: "grid", gap: 6, fontSize: 14, marginBottom: 16 }}>
        <div>{dayjs(a.start).format("ddd D MMM YYYY, HH:mm")} - {dayjs(a.end).format("HH:mm")}</div>
        <div style={{ color: "var(--muted)" }}>{a.staff_name}{propName ? ` · ${propName}` : ""}</div>
        {a.notes && <div style={{ color: "var(--muted)" }}>{a.notes}</div>}
        <StatusPill status={a.status} />
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {a.type === "Inspection" && a.property_id && <button className="btn-ink" style={{ padding: "8px 14px", display: "inline-flex", gap: 6, alignItems: "center" }} onClick={() => onStartInspection(a)} data-testid="appt-start-inspection"><ClipboardCheck size={15} /> Start inspection</button>}
        {a.status !== "completed" && <button className="btn-outline" style={{ padding: "8px 14px", display: "inline-flex", gap: 6, alignItems: "center" }} onClick={() => onStatus(a, "completed")} data-testid="appt-complete"><CheckCircle2 size={15} /> Mark complete</button>}
        <button className="btn-outline" style={{ padding: "8px 14px" }} onClick={() => onEdit(a)} data-testid="appt-edit">Edit</button>
        <button className="btn-outline" style={{ padding: "8px 14px", color: "#9a3b3b", display: "inline-flex", gap: 6, alignItems: "center" }} onClick={() => onDelete(a.appointment_id)} data-testid="appt-delete"><Trash2 size={15} /> Delete</button>
      </div>
    </Modal>
  );
}

function ConflictModal({ data, onCancel, onForce }) {
  return (
    <Modal onClose={onCancel} testid="conflict-modal">
      <div className="lbl" style={{ color: "#8a5a1f", marginBottom: 10 }}>⚠ Scheduling clash</div>
      <p style={{ fontSize: 14, color: "var(--muted)", marginTop: 0 }}>This overlaps with existing appointment(s) for the same staff member or property:</p>
      <div style={{ display: "grid", gap: 8, margin: "12px 0" }}>
        {data.list.map((c) => (
          <div key={c.appointment_id} style={{ fontSize: 13, borderLeft: "3px solid #c68a3a", paddingLeft: 10 }}>
            <b>{c.title}</b><br />{dayjs(c.start).format("ddd D MMM, HH:mm")}-{dayjs(c.end).format("HH:mm")} · {c.staff_name}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn-ink" style={{ padding: "8px 16px" }} onClick={onCancel} data-testid="conflict-cancel">Pick another time</button>
        <button className="btn-outline" style={{ padding: "8px 16px" }} onClick={onForce} data-testid="conflict-force">Schedule anyway</button>
      </div>
    </Modal>
  );
}
